// Programs content — sourced from https://www.worldfarmers.org/programs/
// Faithful to the source (lightly edited for spacing/typos). Phase I copy is
// reconstructed (the source page duplicated Phase II text into Phase I).
// All imagery is locally downloaded from worldfarmers.org (no stock / no AI).

/* ------------------------- Programs landing ------------------------- */
export const PROGRAMS_INTRO = {
  eyebrow: "Our programs",
  title: "How we grow farmers",
  lede:
    "Our farmer-development model engages with farmers according to their needs \u2014 from their first season to establishing a fully independent business \u2014 through peer learning, targeted training, and resource access.",
  image: "/images/wf/vlai/vlai-3483.jpg",
  imageAlt:
    "A farmer sorts a pile of freshly cut greens at the edge of a field.",
};

export const PROGRAMS_OVERVIEW = [
  {
    id: "flats-mentor-farm",
    eyebrow: "The land",
    title: "Flats Mentor Farm",
    body:
      "A 70-acre river-bottom parcel in Lancaster, Massachusetts, where small farmers of diverse ethnic backgrounds access the land, infrastructure, and marketing support to build successful, sustainable farming enterprises \u2014 with hands-on training in soil, irrigation, pest management, and markets.",
    cta: { label: "Explore the Flats Mentor Farm", href: "/programs/flats-mentor-farm" },
    image: "/images/wf/vlai/vlai-3693.jpg",
    imageAlt:
      "A field of kale with farmers working in the distance.",
  },
  {
    id: "mentorship",
    eyebrow: "The journey",
    title: "The Mentorship Program",
    body:
      "A structured five-phase farmer-development model that supports immigrant and refugee farmers at every stage \u2014 from their first subsistence season to fully independent farm businesses \u2014 with the resources, mentorship, and training they need to thrive.",
    cta: { label: "Explore the Mentorship Program", href: "/programs/mentorship" },
    image: "/images/wf/vlai/vlai-3765.jpg",
    imageAlt: "A farmer tends tall trellised vines under a blue sky with scattered clouds.",
  },
];

/* ------------------------- Flats Mentor Farm ------------------------- */
export const FLATS = {
  hero: {
    eyebrow: "Program \u00b7 The land",
    title: "Flats Mentor Farm",
    lede:
      "A shared home for small farmers of diverse ethnic backgrounds \u2014 land, infrastructure, and marketing support on a 70-acre river-bottom parcel in Lancaster, Massachusetts.",
    image: "/images/wf/vlai/vlai-3693.jpg",
    imageAlt:
      "A field of kale with farmers working in the distance.",
  },
  intro: {
    eyebrow: "About the Flats",
    title: "Land, infrastructure & marketing for successful farms",
    paragraphs: [
      "The Flats Mentor Farm (FMF, or \u201CThe Flats\u201D) is located on a 70-acre river-bottom parcel of land in Lancaster, Massachusetts. FMF assists and supports small farmers of diverse ethnic backgrounds with the land, farming infrastructure, and marketing assistance needed to promote and sustain successful farming enterprises.",
      "FMF promotes economically viable agricultural production that protects the environment through the practice of sustainable farming methods. With the help of a farm manager, a sustainability coordinator, and the entire World Farmers team, farmers at the Flats have access to shared and individual resources, as well as hands-on training and technical assistance.",
    ],
    image: "/images/wf/vlai/vlai-3563.jpg",
    imageAlt: "A hand cultivator stands in a freshly tilled plot ready for planting.",
  },
  stats: [
    { id: "acres", value: "70", label: "Acres of river-bottom farmland" },
    { id: "since", value: "1985", label: "Farming at this location since" },
    { id: "markets", value: "6 \u2192 40", label: "Farmers\u2019 markets served (2005 \u2192 2017)" },
  ],
  offers: {
    eyebrow: "What the Flats provides",
    title: "Everything a farmer needs to get growing",
    items: [
      { title: "Land & infrastructure", body: "Access to shared and individual plots, water, and the farming infrastructure to grow at scale." },
      { title: "Hands-on training", body: "Technical assistance in soil fertility, irrigation, and pest and weed management." },
      { title: "Marketing assistance", body: "Support connecting culturally relevant produce to farmers\u2019 markets and wholesale buyers." },
      { title: "Sustainable methods", body: "Economically viable production that protects the environment through sustainable farming practices." },
      { title: "On-site team", body: "A farm manager, sustainability coordinator, and the full World Farmers team on the ground." },
      { title: "A community", body: "A respectful environment of cross-cultural co-learning among farmers, staff, and interns." },
    ],
  },
  history: {
    eyebrow: "How it grew",
    title: "From a few families to a regional model",
    body:
      "Immigrant and refugee farmers have been farming at this location since 1985. In 2005, after many trials and tribulations, the farmers formally organized to become the Flats Mentor Farm. Since then \u2014 with the support of Heifer International, the University of Massachusetts Amherst, the Society for the Promotion of Agriculture, the USDA, and many others \u2014 the farmers at FMF have evolved from growing enough for their immediate and extended families to selling their produce at farmers\u2019 markets and through cooperative wholesale across the state. The number of farmers selling at these markets has increased each year, from just 6 farmers\u2019 markets in 2005 to 40 in 2017.",
    image: "/images/wf/farmer10.jpg",
    imageAlt: "A farmer in a floral sun hat carefully tending leafy green crops.",
  },
};

/* ------------------------- Mentorship Program ------------------------- */
export const MENTORSHIP = {
  hero: {
    eyebrow: "Program \u00b7 The journey",
    title: "The Mentorship Program",
    lede:
      "A structured five-phase farmer-development model designed to support immigrant and refugee farmers from their first desire to enter our program all the way to full, independent farm ownership.",
    image: "/images/wf/vlai/vlai-3764.jpg",
    imageAlt: "A farmer walks among tall trellised vines beneath a bright summer sky.",
  },
  intro: {
    eyebrow: "How mentorship works",
    title: "A path that meets farmers where they are",
    body:
      "World Farmers employs a structured five-phase farmer-development model designed to support immigrant and refugee farmers from initial interest in our program to full independent farmer. This process ensures that farmers receive the resources, mentorship, and training they need to thrive at each stage of their journey \u2014 and to join our agricultural community.",
  },
  phases: [
    {
      n: 1,
      title: "First-Year & Subsistence Farmers",
      details:
        "Farmers enter the World Farmers community and grow primarily for their own families and extended community as they learn New England\u2019s land, climate, and growing season. This foundational phase builds familiarity, skills, and confidence before scaling up.",
      image: "/images/wf/vlai/vlai-3851.jpg",
      imageAlt: "A farmer kneels among leafy plants to harvest.",
    },
    {
      n: 2,
      title: "Increased Land & Production Farmers",
      details:
        "The next step in farm development. Farmers who decide to scale up by expanding their land base enter Phase II. They participate in peer-to-peer learning through farmer exchanges and access targeted trainings, building their capacity for larger-scale production. Farmers review their increased land and develop production plans, as well as a plan for the use of the crops resulting from the land increase.",
      image: "/images/wf/vlai/vlai-3541.jpg",
      imageAlt: "Tall trellised vines form a green corridor through the field.",
    },
    {
      n: 3,
      title: "Market-Ready Farmers",
      details:
        "Farmers begin exploring marketing and sales, usually beginning within their own cultural communities. They are supported in crop planning and recordkeeping, and receive technical assistance in crop quality, safe harvest and post-harvest handling, and introductions to local mainstream markets by World Farmers. This phase marks the shift from household production to entry-level marketing and business development.",
      image: "/images/wf/vlai/vlai-3626.jpg",
      imageAlt: "A farmer kneels beside a basket filling with freshly picked peppers.",
    },
    {
      n: 4,
      title: "Commercial Farmers",
      details:
        "Farmers in this phase have gained the skills and training necessary to operate their own farm businesses. They possess the financial literacy required to secure farm ownership and equipment loans. While independent in many aspects, these farmers continue to access World Farmers\u2019 mentorship and resources as they expand into new markets and strengthen their operations.",
      image: "/images/wf/vlai/vlai-3799.jpg",
      imageAlt: "A farmer in a pink apron pauses in the field holding a freshly picked gourd.",
    },
    {
      n: 5,
      title: "Independent Farmers",
      details:
        "Farmers in this phase are independent operators, managing their own land or demonstrating the capacity to do so. They may continue to benefit from World Farmers\u2019 technical assistance, market access support, and connections to external networks. World Farmers\u2019 role shifts from direct management to long-term partnership, ensuring these farmers remain part of a supportive agricultural ecosystem.",
      image: "/images/wf/vlai/vlai-3767.jpg",
      imageAlt: "A farmer stands among tall trellised vines under a blue sky.",
    },
  ],
};
